/**
 * PostHog Provider Component
 * Ensures PostHog is properly initialized and provides tracking context.
 * PostHog + Session Replay apply to ALL templates (BaseTemplate, BioLinkTemplate, ArgentinaBioTemplate).
 */

import React from 'react';

interface PostHogProviderProps {
  children: React.ReactNode;
}

export const PostHogProvider: React.FC<PostHogProviderProps> = ({ children }) => {
  // Simple provider that just renders children
  // PostHog initialization is handled globally in main.tsx
  return <>{children}</>;
};

// Safe PostHog hook with error handling
export const usePostHog = () => {
  const [status, setStatus] = React.useState({
    isInitialized: false,
    isRecording: false,
    distinctId: null,
    sessionId: null
  });

  React.useEffect(() => {
    try {
      const checkStatus = () => {
        if (typeof window !== 'undefined' && window.posthog) {
          setStatus({
            isInitialized: !!window.posthog.__loaded,
            isRecording: window.posthog.sessionRecordingStarted?.() || false,
            distinctId: window.posthog.get_distinct_id?.() || null,
            sessionId: window.posthog.get_session_id?.() || null
          });
        }
      };

      checkStatus();
      const interval = setInterval(checkStatus, 2000); // Check every 2 seconds

      return () => clearInterval(interval);
    } catch {
      // intentional
    }
  }, []);

  return {
    ...status,
    startRecording: () => {
      try {
        if (window.posthog?.startSessionRecording) {
          window.posthog.startSessionRecording();
        }
      } catch {
        // intentional
      }
    },
    stopRecording: () => {
      try {
        if (window.posthog?.stopSessionRecording) {
          window.posthog.stopSessionRecording();
        }
      } catch {
        // intentional
      }
    },
    debugStatus: () => {
      try {
        if (window.posthog && import.meta.env.DEV) {
          console.log("PostHog status", {
            loaded: !!window.posthog.__loaded,
            distinctId: window.posthog.get_distinct_id?.(),
            sessionId: window.posthog.get_session_id?.(),
            recording: window.posthog.sessionRecordingStarted?.(),
          });
        }
      } catch {
        // intentional
      }
    }
  };
};

export const withPostHogTracking = (Component: React.ComponentType) => Component;

export const usePostHogFeatureFlag = (flag: string) => {
  try {
    return window.posthog?.getFeatureFlag?.(flag) || false;
  } catch (error) {
    return false;
  }
};

export const usePostHogTracking = () => ({
  trackTemplateUsage: (template: string, data?: Record<string, unknown>) => {
    try {
      if (window.posthog?.capture) {
        window.posthog.capture('template_usage', { template, ...data });
      }
    } catch {
      // intentional
    }
  },
  trackButtonClick: (buttonId: string, data?: Record<string, unknown>) => {
    try {
      if (window.posthog?.capture) {
        window.posthog.capture('button_click', { button_id: buttonId, ...data });
      }
    } catch {
      // intentional
    }
  },
  trackSocialClick: (platform: string, data?: Record<string, unknown>) => {
    try {
      if (window.posthog?.capture) {
        window.posthog.capture('social_click', { platform, ...data });
      }
    } catch {
      // intentional
    }
  },
  trackConversion: (type: string, data?: Record<string, unknown>) => {
    try {
      if (window.posthog?.capture) {
        window.posthog.capture('conversion', { conversion_type: type, ...data });
      }
    } catch {
      // intentional
    }
  },
  trackEnhancedButtonClick: (buttonData: Record<string, unknown>) => {
    try {
      if (window.posthog?.capture) {
        window.posthog.capture('enhanced_button_click', buttonData);
      }
    } catch {
      // intentional
    }
  }
});

export default PostHogProvider;